import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PageTemplate from "../../components/PageTemplate";
import { useTranslation } from "react-i18next";
import http from "../../api/apiClient";
import "../../styles/parent-portal.css";

const ParentAlumniPreferences = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const childId = searchParams.get("childId");
  const [childName, setChildName] = useState("");
  const normalizeDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val).slice(0, 10);
    return d.toISOString().slice(0, 10);
  };

  const [childPrefs, setChildPrefs] = useState({
    allowStore: true,
    name: "",
    dateBirth: "",
    email: "",
  });

  const [parentPrefs, setParentPrefs] = useState({
    allowStore: true,
    newsletter: false,
    alumni: false,
    activities: false,
    name: "",
    email: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [parentId, setParentId] = useState(null);
  const parentPrefsKey = parentId ? `pp-alumni-parent-${parentId}` : null;
  const childPrefsKey = childId ? `pp-alumni-child-${childId}` : null;

  useEffect(() => {
    // fetch current parent to key preferences per parent
    (async () => {
      try {
        const { data } = await http.get("/parent-portal/me");
        if (data?.id) setParentId(data.id);
        const fullName = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
        setParentPrefs((prev) => ({
          ...prev,
          name: fullName || prev.name,
          email: data?.email || prev.email,
        }));
      } catch (e) {
        // ignore; fallback prevents per-parent storage
      }
    })();
  }, []);

  useEffect(() => {
    // Load stored parent preferences (local storage placeholder until backend is ready)
    if (parentPrefsKey) {
      try {
        const stored = localStorage.getItem(parentPrefsKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          setParentPrefs((prev) => ({ ...prev, ...parsed }));
        }
      } catch (e) {
        // ignore
      }
    }

    // Load stored child preferences per child if available
    if (childPrefsKey) {
      try {
        const storedChild = localStorage.getItem(childPrefsKey);
        if (storedChild) {
          const parsedChild = JSON.parse(storedChild);
          setChildPrefs((prev) => ({ ...prev, ...parsedChild }));
        }
      } catch (e) {
        // ignore
      }
    }
  }, [childPrefsKey, parentPrefsKey]);

  useEffect(() => {
    if (!childId) return;

    (async () => {
      try {
      const { data } = await http.get(`/children/${childId}`);
        const name = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
        if (name) {
          setChildName(name);
          setChildPrefs((prev) => ({ ...prev, name }));
        }
        const dateVal = data?.dateBirth || data?.dateOfBirth;
        const normalizedDate = normalizeDate(dateVal);
        if (normalizedDate) {
          setChildPrefs((prev) => ({ ...prev, dateBirth: normalizedDate }));
        }
        if (data?.email) {
          setChildPrefs((prev) => ({ ...prev, email: data.email }));
        }
      } catch (err) {
        // silently ignore; fallback is ID
      }
    })();
  }, [childId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(false);
    setSubmitError(null);
    setSaving(true);
    if (!parentId) {
      setSubmitError(
        t("parentPortal.alumni.error_missing_parent", {
          defaultValue: "Parent information is required to save preferences.",
        })
      );
      setSaving(false);
      return;
    }
    const anyParentPref = !!(parentPrefs.newsletter || parentPrefs.alumni || parentPrefs.activities);

    const payload = {
      childId: childId || null,
      parentId,
      child: {
        allowStore: !!childPrefs.allowStore,
        name: childPrefs.name || childName || null,
        dateBirth: childPrefs.dateBirth || null,
        email: childPrefs.email || null,
      },
      parents: {
        allowStore: !!parentPrefs.allowStore && anyParentPref,
        newsletter: !!parentPrefs.newsletter,
        alumni: !!parentPrefs.alumni,
        activities: !!parentPrefs.activities,
        name: parentPrefs.name || null,
        email: parentPrefs.email || null,
      },
    };

    (async () => {
      try {
        await http.post("/alumni", payload);
        if (parentPrefsKey) {
          if (payload.parents.allowStore) {
            localStorage.setItem(parentPrefsKey, JSON.stringify(parentPrefs));
          } else {
            localStorage.removeItem(parentPrefsKey);
          }
        }
        if (childPrefsKey) {
          localStorage.setItem(childPrefsKey, JSON.stringify(childPrefs));
        }
        setSubmitted(true);
      } catch (err) {
        setSubmitError(
          err?.response?.data?.message ||
            err?.message ||
            t("parentPortal.alumni.save_failed", { defaultValue: "Saving preferences failed." })
        );
      } finally {
        setSaving(false);
      }
    })();
  };

  const resetForm = () => {
    setSubmitted(false);
    setChildPrefs((prev) => ({
      ...prev,
      allowStore: true,
    }));
    setParentPrefs({
      allowStore: true,
      newsletter: false,
      alumni: false,
      activities: false,
    });
  };

  const heading = useMemo(
    () =>
      t("parentPortal.alumni.title", {
        defaultValue: "Alumni preferences",
      }),
    [t]
  );

  return (
    <PageTemplate title="" wrapperClassName="max-w-7xl mx-auto">
      <div className="parent-glass-card border border-white/60 bg-white/90 p-8 text-gray-900 shadow-[0_25px_60px_rgba(0,0,0,0.12)] dark:border-slate-800/60 dark:bg-slate-900/70 dark:text-gray-100 space-y-5">
        <div className="rounded-3xl border border-emerald-100/70 bg-emerald-50/80 p-5 shadow-sm dark:border-emerald-700/40 dark:bg-emerald-900/40">
          <h2 className="text-2xl font-semibold text-emerald-900 dark:text-emerald-100">{heading}</h2>
          <p className="text-sm text-gray-800 dark:text-emerald-100">
            {t("parentPortal.alumni.intro", {
              defaultValue:
                "Let us know whether we may keep your child on our alumni list and how we may contact you.",
            })}
          </p>
          {childId && (
            <p className="mt-1 text-xs text-gray-600 dark:text-emerald-200">
              {t("parentPortal.alumni.child_hint", { defaultValue: "For child:" })}{" "}
              {childName || childId}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-5 shadow-sm dark:border-emerald-700/40 dark:bg-slate-900/70">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-200">
                  {t("parentPortal.alumni.child_section", { defaultValue: "Child" })}
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-200">
                  {t("parentPortal.alumni.child_helper", {
                    defaultValue: "Allow us to keep basic info for the alumni list.",
                  })}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-emerald-800 dark:text-emerald-100">
                <input
                  type="checkbox"
                  checked={childPrefs.allowStore}
                  onChange={(e) => setChildPrefs((prev) => ({ ...prev, allowStore: e.target.checked }))}
                />
                {t("parentPortal.alumni.allow_store_child", { defaultValue: "Allow storing for alumni list" })}
              </label>
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-100">
                  {t("parentPortal.alumni.child_full_name", { defaultValue: "Full name" })}
                </label>
                <input
                  type="text"
                  value={childPrefs.name}
                  onChange={(e) => setChildPrefs((prev) => ({ ...prev, name: e.target.value }))}
                  className="parent-input bg-white text-gray-900 dark:bg-slate-900/60 dark:text-white rounded-2xl px-4 py-3 shadow-inner"
                  placeholder={t("parentPortal.alumni.child_name_placeholder", { defaultValue: "Name" })}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-100">
                  {t("children.common.date_of_birth", { defaultValue: "Date of Birth" })}
                </label>
                <input
                  type="date"
                  value={childPrefs.dateBirth}
                  onChange={(e) => setChildPrefs((prev) => ({ ...prev, dateBirth: e.target.value }))}
                  placeholder="dd/mm/yyyy"
                  inputMode="numeric"
                  className="parent-input bg-white text-gray-900 dark:bg-slate-900/60 dark:text-white rounded-2xl px-4 py-3 shadow-inner"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-100">
                  {t("parentPortal.alumni.child_email", { defaultValue: "Email (optional)" })}
                </label>
                <input
                  type="email"
                  value={childPrefs.email}
                  onChange={(e) => setChildPrefs((prev) => ({ ...prev, email: e.target.value }))}
                  className="parent-input bg-white text-gray-900 dark:bg-slate-900/60 dark:text-white rounded-2xl px-4 py-3 shadow-inner"
                  placeholder="name@example.com"
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-amber-100/70 bg-white/90 p-5 shadow-sm dark:border-amber-700/40 dark:bg-slate-900/70">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-200">
                  {t("parentPortal.alumni.parents_section", { defaultValue: "Parents" })}
                </p>
                <p className="text-sm text-gray-700 dark:text-slate-200">
                  {t("parentPortal.alumni.parents_helper", {
                    defaultValue: "Choose if we may keep your details and how we can reach out.",
                  })}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-100">
                <input
                  type="checkbox"
                  checked={parentPrefs.allowStore}
                  onChange={(e) => setParentPrefs((prev) => ({ ...prev, allowStore: e.target.checked }))}
                />
                {t("parentPortal.alumni.allow_store_parents", { defaultValue: "Allow storing parents" })}
              </label>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {[
                { key: "newsletter", label: t("parentPortal.alumni.opt_in_newsletter", { defaultValue: "Newsletter" }) },
                { key: "alumni", label: t("parentPortal.alumni.opt_in_alumni", { defaultValue: "Alumni updates" }) },
                { key: "activities", label: t("parentPortal.alumni.opt_in_activities", { defaultValue: "Activities" }) },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 rounded-xl border border-amber-100/70 bg-amber-50/70 px-3 py-2 text-sm font-semibold text-amber-800 shadow-sm dark:border-amber-700/40 dark:bg-amber-900/30 dark:text-amber-100 min-h-[44px]"
                >
                  <input
                    type="checkbox"
                    checked={parentPrefs[key]}
                    onChange={(e) => setParentPrefs((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" className="parent-form-button parent-form-button-submit">
              {saving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/portal")}
              className="parent-form-button parent-form-button-reset"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="parent-form-button parent-form-button-secondary bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-100"
            >
              {t("common.reset", { defaultValue: "Reset" })}
            </button>
            {submitError && (
              <span className="text-sm text-red-600 dark:text-red-300">
                {submitError}
              </span>
            )}
          </div>
        </form>
      </div>
    </PageTemplate>
  );
};

export default ParentAlumniPreferences;
